import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getActiveTradeGroupsDetails } from '../api/tradeGroups';
import ActiveTradeGroupsTable from './metrics/ActiveTradeGroupsTable';
import TradeGroupWizard from './TradeGroupWizard';
import DashboardBottomLogo from './DashboardBottomLogo';
import { TrendingUpIcon, TargetIcon, ActivityIcon, ChartBarIcon, BollingerBandsIcon, RsiExtremesIcon, MacdIcon, VwapIcon, IchimokuCloudIcon } from './shared/Icons';
import SectionTitle from './shared/SectionTitle';
import { useGlobalAutoRefreshStore, useAutoRefreshTrigger } from '../stores/useGlobalAutoRefreshStore';
import './TradeGroups.css';

const TradeGroups: React.FC = () => {
  const queryClient = useQueryClient();
  const setActiveDashboard = useGlobalAutoRefreshStore((state) => state.setActiveDashboard);
  const refreshTrigger = useAutoRefreshTrigger('trade-groups');
  const [showWizard, setShowWizard] = useState(false);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string[]>([]);

  // Set this dashboard as active when mounted, clear when unmounted
  useEffect(() => {
    setActiveDashboard('trade-groups');
    return () => setActiveDashboard(null);
  }, [setActiveDashboard]);

  // When global refresh trigger changes, invalidate all queries to refetch data
  useEffect(() => {
    if (refreshTrigger > 0) {
      queryClient.invalidateQueries();
    }
  }, [refreshTrigger, queryClient]);

  // Fetch trade groups data to get counts per type
  const { data } = useQuery({
    queryKey: ['active-trade-groups-details'],
    queryFn: getActiveTradeGroupsDetails,
    retry: 2,
  });

  // Calculate counts for each strategy type (using isActive field)
  const regularLadderCount = data?.tradeGroups.filter(tg =>
    tg.tradeGroupType === 'REGULAR_LADDER' && tg.isActive
  ).length || 0;

  const futureLadderCount = data?.tradeGroups.filter(tg =>
    tg.tradeGroupType === 'FUTURE_LADDER' && tg.isActive
  ).length || 0;

  const momentumSwingCount = data?.tradeGroups.filter(tg =>
    tg.tradeGroupType === 'MOMENTUM_SWING' && tg.isActive
  ).length || 0;

  const volatilityLadderCount = data?.tradeGroups.filter(tg =>
    tg.tradeGroupType === 'VOLATILITY_LADDER' && tg.isActive
  ).length || 0;

  const bollingerReversionCount = data?.tradeGroups.filter(tg =>
    tg.tradeGroupType === 'BOLLINGER_REVERSION' && tg.isActive
  ).length || 0;

  const rsiExtremesCount = data?.tradeGroups.filter(tg =>
    tg.tradeGroupType === 'RSI_EXTREMES' && tg.isActive
  ).length || 0;

  const macdReversionCount = data?.tradeGroups.filter(tg =>
    tg.tradeGroupType === 'MACD_REVERSION' && tg.isActive
  ).length || 0;

  const vwapReversionCount = data?.tradeGroups.filter(tg =>
    tg.tradeGroupType === 'VWAP_REVERSION' && tg.isActive
  ).length || 0;

  const ichimokuCloudCount = data?.tradeGroups.filter(tg =>
    tg.tradeGroupType === 'ICHIMOKU_CLOUD' && tg.isActive
  ).length || 0;

  const handleStrategyCardClick = (strategyType: string) => {
    // Toggle filter: if already selected, clear it; otherwise, set it
    if (selectedTypeFilter.includes(strategyType)) {
      setSelectedTypeFilter([]);
    } else {
      setSelectedTypeFilter([strategyType]);
    }
  };

  const handleWizardSuccess = (tradeGroupId: string) => {
    console.log('Trade group created:', tradeGroupId);
    setShowWizard(false);
    alert(`Trade group created successfully! ID: ${tradeGroupId}`);
  };

  return (
    <div className="trade-groups">
      <div className="dashboard-header">
        <SectionTitle
          title="Trade Group Management"
        />
        <button className="primary-button" onClick={() => setShowWizard(true)}>
          + Create New Trade Group
        </button>
      </div>

      <div className="strategy-types">
        <div
          className={`strategy-card ${selectedTypeFilter.includes('REGULAR_LADDER') ? 'active-filter' : ''}`}
          onClick={() => handleStrategyCardClick('REGULAR_LADDER')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStrategyCardClick('REGULAR_LADDER')}
        >
          <div className="strategy-icon"><ChartBarIcon size={32} /></div>
          <h3>Regular Ladder</h3>
          <p>Traditional DCA strategy for market dips</p>
          <div className="strategy-stats">Active: {regularLadderCount}</div>
        </div>

        <div
          className={`strategy-card ${selectedTypeFilter.includes('FUTURE_LADDER') ? 'active-filter' : ''}`}
          onClick={() => handleStrategyCardClick('FUTURE_LADDER')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStrategyCardClick('FUTURE_LADDER')}
        >
          <div className="strategy-icon"><TargetIcon size={32} /></div>
          <h3>Future Ladder</h3>
          <p>Conditional trades above current price</p>
          <div className="strategy-stats">Active: {futureLadderCount}</div>
        </div>

        <div
          className={`strategy-card ${selectedTypeFilter.includes('MOMENTUM_SWING') ? 'active-filter' : ''}`}
          onClick={() => handleStrategyCardClick('MOMENTUM_SWING')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStrategyCardClick('MOMENTUM_SWING')}
        >
          <div className="strategy-icon"><TrendingUpIcon size={32} /></div>
          <h3>Momentum Swing</h3>
          <p>Trend-following with swing optimization</p>
          <div className="strategy-stats">Active: {momentumSwingCount}</div>
        </div>

        <div
          className={`strategy-card ${selectedTypeFilter.includes('VOLATILITY_LADDER') ? 'active-filter' : ''}`}
          onClick={() => handleStrategyCardClick('VOLATILITY_LADDER')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStrategyCardClick('VOLATILITY_LADDER')}
        >
          <div className="strategy-icon"><ActivityIcon size={32} /></div>
          <h3>Volatility Ladder</h3>
          <p>Adaptive position sizing based on volatility</p>
          <div className="strategy-stats">Active: {volatilityLadderCount}</div>
        </div>

        <div
          className={`strategy-card ${selectedTypeFilter.includes('BOLLINGER_REVERSION') ? 'active-filter' : ''}`}
          onClick={() => handleStrategyCardClick('BOLLINGER_REVERSION')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStrategyCardClick('BOLLINGER_REVERSION')}
        >
          <div className="strategy-icon"><BollingerBandsIcon size={32} /></div>
          <h3>Bollinger Reversion</h3>
          <p>Mean reversion on lower band touches in ranging markets</p>
          <div className="strategy-stats">Active: {bollingerReversionCount}</div>
        </div>

        <div
          className={`strategy-card strategy-card-rsi ${selectedTypeFilter.includes('RSI_EXTREMES') ? 'active-filter' : ''}`}
          onClick={() => handleStrategyCardClick('RSI_EXTREMES')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStrategyCardClick('RSI_EXTREMES')}
        >
          <div className="strategy-icon"><RsiExtremesIcon size={32} /></div>
          <h3>RSI Extremes</h3>
          <p>Mean reversion on RSI oversold in low volatility markets</p>
          <div className="strategy-stats">Active: {rsiExtremesCount}</div>
        </div>

        <div
          className={`strategy-card strategy-card-macd ${selectedTypeFilter.includes('MACD_REVERSION') ? 'active-filter' : ''}`}
          onClick={() => handleStrategyCardClick('MACD_REVERSION')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStrategyCardClick('MACD_REVERSION')}
        >
          <div className="strategy-icon"><MacdIcon size={32} /></div>
          <h3>MACD Reversion</h3>
          <p>Mean reversion on MACD histogram recovery</p>
          <div className="strategy-stats">Active: {macdReversionCount}</div>
        </div>

        <div
          className={`strategy-card strategy-card-vwap ${selectedTypeFilter.includes('VWAP_REVERSION') ? 'active-filter' : ''}`}
          onClick={() => handleStrategyCardClick('VWAP_REVERSION')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStrategyCardClick('VWAP_REVERSION')}
        >
          <div className="strategy-icon"><VwapIcon size={32} /></div>
          <h3>VWAP Reversion</h3>
          <p>Volume-weighted mean reversion; entry on σ-band lower touch</p>
          <div className="strategy-stats">Active: {vwapReversionCount}</div>
        </div>

        <div
          className={`strategy-card strategy-card-ichimoku ${selectedTypeFilter.includes('ICHIMOKU_CLOUD') ? 'active-filter' : ''}`}
          onClick={() => handleStrategyCardClick('ICHIMOKU_CLOUD')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleStrategyCardClick('ICHIMOKU_CLOUD')}
        >
          <div className="strategy-icon"><IchimokuCloudIcon size={32} /></div>
          <h3>Ichimoku Cloud</h3>
          <p>Trend-following long entries on 5-component confirmation above the cloud</p>
          <div className="strategy-stats">Active: {ichimokuCloudCount}</div>
        </div>
      </div>

      {/* Active Trade Groups Table */}
      <div className="active-trade-groups-section">
        <ActiveTradeGroupsTable
          selectedTypeFilter={selectedTypeFilter}
          onTypeFilterChange={setSelectedTypeFilter}
        />
      </div>

      {/* Trade Group Creation Wizard Modal */}
      {showWizard && (
        <TradeGroupWizard
          onClose={() => setShowWizard(false)}
          onSuccess={handleWizardSuccess}
        />
      )}

      <DashboardBottomLogo />
    </div>
  );
};

export default TradeGroups;
